import { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import api from '../services/api';

interface Scenario {
  id: string;
  scenario_name: string;
  difficulty: string;
  context: string;
  question_count: number;
  attempt_count: number;
  best_score: number | null;
}

const DocumentTypePage: React.FC = () => {
  const { typeId } = useParams<{ typeId: string }>();
  const [scenarios, setScenarios] = useState<Scenario[]>([]);
  const [documentType, setDocumentType] = useState<any>(null);
  const [loading, setLoading] = useState(true);
  const navigate = useNavigate();

  useEffect(() => {
    if (typeId) {
      fetchScenarios();
      fetchDocumentType();
    }
  }, [typeId]);

  const fetchDocumentType = async () => {
    try {
      const response = await api.get(`/documents/types/${typeId}`);
      setDocumentType(response.data);
    } catch (error) {
      console.error('Error fetching document type:', error);
    }
  };

  const fetchScenarios = async () => {
    try {
      const response = await api.get(`/documents/types/${typeId}/scenarios`);
      setScenarios(response.data);
    } catch (error) {
      console.error('Error fetching scenarios:', error);
    } finally {
      setLoading(false);
    }
  };

  const getDifficultyColor = (difficulty: string): string => {
    switch (difficulty) {
      case 'easy':
        return 'bg-green-500/20 text-green-300 border-green-500/30';
      case 'medium':
        return 'bg-yellow-500/20 text-yellow-300 border-yellow-500/30';
      case 'hard':
        return 'bg-red-500/20 text-red-300 border-red-500/30';
      default:
        return 'bg-slate-500/20 text-slate-300 border-slate-500/30';
    }
  };

  const getDifficultyIcon = (difficulty: string): string => {
    switch (difficulty) {
      case 'easy':
        return '🟢';
      case 'medium':
        return '🟡';
      case 'hard':
        return '🔴';
      default:
        return '⚪';
    }
  };

  const getScoreColor = (score: number, total: number): string => {
    const percentage = (score / total) * 100;
    if (percentage === 100) return 'text-green-400';
    if (percentage >= 70) return 'text-yellow-400';
    return 'text-red-400';
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-slate-950 flex items-center justify-center">
        <div className="text-xl text-white">Loading scenarios...</div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-slate-950">
      {/* Header */}
      <header className="border-b border-primary-500/20 bg-slate-900/95 sticky top-0 z-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center h-16">
            <button
              onClick={() => navigate('/documents')}
              className="text-slate-400 hover:text-white transition-colors"
            >
              ← Back to Documents
            </button>
          </div>
        </div>
      </header>

      {/* Main Content */}
      <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Document Type Header */}
        {documentType && (
          <div className="mb-8">
            <div className="flex items-center gap-4 mb-4">
              <div className="w-16 h-16 bg-primary-500/20 rounded-xl flex items-center justify-center">
                <span className="text-4xl">📋</span>
              </div>
              <div>
                <div className="text-primary-400 text-sm font-semibold mb-1">
                  {documentType.form_number}
                </div>
                <h1 className="text-3xl font-bold">{documentType.name}</h1>
              </div>
            </div>
            <p className="text-slate-400 text-lg">{documentType.description}</p>
          </div>
        )}

        {/* Scenarios Grid */}
        <div className="mb-8">
          <h2 className="text-2xl font-bold mb-6">Practice Scenarios</h2>
          
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            {scenarios.map((scenario) => {
              const percentage = scenario.best_score !== null
                ? Math.round((scenario.best_score / scenario.question_count) * 100)
                : null;

              return (
                <div
                  key={scenario.id}
                  onClick={() => navigate(`/documents/scenarios/${scenario.id}`)}
                  className="card hover:border-primary-500/50 transition-all cursor-pointer"
                >
                  {/* Scenario Header */}
                  <div className="flex items-start justify-between mb-4">
                    <div className="flex-1">
                      <h3 className="text-xl font-bold mb-2">{scenario.scenario_name}</h3>
                      <p className="text-sm text-slate-400 mb-3">{scenario.context}</p>
                    </div>
                    <div className={`px-3 py-1 rounded-full text-xs font-semibold border ${getDifficultyColor(scenario.difficulty)}`}>
                      {getDifficultyIcon(scenario.difficulty)} {scenario.difficulty.toUpperCase()}
                    </div>
                  </div>

                  {/* Stats */}
                  <div className="grid grid-cols-3 gap-4 mb-4">
                    <div>
                      <div className="text-slate-400 text-xs mb-1">Questions</div>
                      <div className="text-white font-semibold">{scenario.question_count}</div>
                    </div>
                    <div>
                      <div className="text-slate-400 text-xs mb-1">Attempts</div>
                      <div className="text-white font-semibold">{scenario.attempt_count}</div>
                    </div>
                    <div>
                      <div className="text-slate-400 text-xs mb-1">Best Score</div>
                      {scenario.best_score !== null ? (
                        <div className={`font-semibold ${getScoreColor(scenario.best_score, scenario.question_count)}`}>
                          {scenario.best_score}/{scenario.question_count}
                          <span className="text-xs ml-1">({percentage}%)</span>
                        </div>
                      ) : (
                        <div className="text-slate-500 text-sm">Not attempted</div>
                      )}
                    </div>
                  </div>

                  {/* Action */}
                  <div className="text-primary-400 text-sm font-semibold">
                    {scenario.attempt_count === 0 ? 'Start Exercise →' : 'Practice Again →'}
                  </div>
                </div>
              );
            })}
          </div>
        </div>

        {scenarios.length === 0 && (
          <div className="text-center text-slate-400 py-12">
            <p className="text-xl">No scenarios available for this document type yet.</p>
          </div>
        )}
      </main>
    </div>
  );
};

export default DocumentTypePage;
